import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Spin, Empty, Collapse, Tag, Popconfirm, message, Select, Alert, Tooltip } from "antd";
import { DeleteOutlined, PlusOutlined, LinkOutlined, AppstoreOutlined, DisconnectOutlined, ExportOutlined } from "@ant-design/icons";
import { ButtonItem, SelectItem, InputNumberItem, InputItem } from "../../../../Components/Items";
import AgregarUbicacion from "../AgregarUbicacion/AgregarUbicacion";
import RackMapaGrid, { rackTieneMedidas, dimensionesRack, cabeEnRack, medidasTexto } from "./RackMapaGrid";

const { Option } = Select;

// A partir de este tamaño la cuadrícula tiene demasiadas celdas para acomodarse en el drawer
const LIMITE_GRID_DRAWER = 10;

//Cuerpo del acomodo de un rack: carga el rack + sus ubicaciones, permite editar su config,
//acomodar visualmente (RackMapaGrid), ligar/agregar/quitar/desligar/deshabilitar ubicaciones. Lo usan
//tanto el drawer (racks chicos) como la página de pantalla completa (racks grandes).
//  - enPagina=false (drawer): botón "Ver mapa" + si el rack es grande (>10x10) pide abrir la página.
//  - enPagina=true (página): el mapa siempre visible y se dibuja aunque sea grande.
const RackAcomodo = ({
  rackId,
  empresaId,
  proyectoId,
  headersToken,
  permisoAgregarUbicacion,
  permisoDeshabilitarUbicacion,
  activo = true,
  enPagina = false,
  onConfigurarEnPagina,
  onRackLoaded,
}) => {
  const [cargando, setCargando] = useState(false);
  const [rack, setRack] = useState(null);
  const [ubicaciones, setUbicaciones] = useState([]);
  const [ubicacionesSinRack, setUbicacionesSinRack] = useState([]);
  const [modalAgregarVisible, setModalAgregarVisible] = useState(false);
  const [ubicacionALigarId, setUbicacionALigarId] = useState(undefined);
  const [ligando, setLigando] = useState(false);
  const [verMapa, setVerMapa] = useState(enPagina);
  const [slotPrecarga, setSlotPrecarga] = useState(null);
  const [config, setConfig] = useState({
    codigo: "", nombre: "", numeroColumnas: 1, numeroNiveles: 1, numeroProfundidades: 1,
    anchoMetros: undefined, altoMetros: undefined, profundidadMetros: undefined,
  });
  const [guardandoConfig, setGuardandoConfig] = useState(false);

  const cargarDetalle = useCallback(() => {
    if (!rackId) return;
    setCargando(true);
    Promise.all([
      axios.post("rack/selectDetalleRackConInsumos", { empresaId, proyectoId, rackId }, { headers: headersToken }),
      axios.post("ubicacionProyecto/selectUbicacionesProyecto", { empresaId, proyectoId, estado: 1 }, { headers: headersToken }),
    ])
      .then(([resDetalle, resUbicaciones]) => {
        if (resDetalle.data.success === 2) {
          setRack(resDetalle.data.rack);
          setUbicaciones(resDetalle.data.ubicaciones || []);
        } else {
          message.error(resDetalle.data.error || "No se pudo consultar el detalle del rack");
        }
        if (resUbicaciones.data.success === 2) {
          setUbicacionesSinRack((resUbicaciones.data.ubicaciones || []).filter((u) => !u.rackId));
        }
      })
      .catch(() => message.error("No se pudo consultar el detalle del rack"))
      .finally(() => setCargando(false));
  }, [rackId, empresaId, proyectoId, headersToken]);

  useEffect(() => {
    if (activo) cargarDetalle();
  }, [activo, cargarDetalle]);

  useEffect(() => {
    if (rack) {
      setConfig({
        codigo: rack.codigo || "",
        nombre: rack.nombre || "",
        numeroColumnas: rack.numeroColumnas || 1,
        numeroNiveles: rack.numeroNiveles || 1,
        numeroProfundidades: rack.numeroProfundidades || 1,
        anchoMetros: rack.anchoMetros,
        altoMetros: rack.altoMetros,
        profundidadMetros: rack.profundidadMetros,
      });
      if (onRackLoaded) onRackLoaded(rack);
    }
  }, [rack, onRackLoaded]);

  const cambiarConfig = (campo, valor) => setConfig((c) => ({ ...c, [campo]: valor }));

  const guardarConfig = () => {
    if (!config.codigo) { message.warning("El código del rack es obligatorio"); return; }
    setGuardandoConfig(true);
    axios
      .post("rack/editarRack", {
        _id: rack._id, codigo: config.codigo, nombre: config.nombre, x: rack.x, y: rack.y, ancho: rack.ancho, alto: rack.alto,
        numeroColumnas: config.numeroColumnas, numeroNiveles: config.numeroNiveles, numeroProfundidades: config.numeroProfundidades,
        anchoMetros: config.anchoMetros, altoMetros: config.altoMetros, profundidadMetros: config.profundidadMetros,
      }, { headers: headersToken })
      .then((res) => {
        if (res.data.success === 2) { message.success("Rack actualizado"); cargarDetalle(); }
        else message.error(res.data.error || "No se pudo actualizar el rack");
      })
      .catch(() => message.error("No se pudo actualizar el rack"))
      .finally(() => setGuardandoConfig(false));
  };

  const deshabilitar = (ubicacionId) => {
    axios.post("ubicacionProyecto/deshabilitarUbicacionProyecto", { _id: ubicacionId }, { headers: headersToken })
      .then((res) => { if (res.data.success === 2) { message.success("Ubicación deshabilitada"); cargarDetalle(); } else message.error(res.data.error || "No se pudo deshabilitar"); })
      .catch(() => message.error("No se pudo deshabilitar la ubicación"));
  };

  const cerrarModalAgregar = () => { setModalAgregarVisible(false); setSlotPrecarga(null); cargarDetalle(); };

  const asignarSlot = (slot, ubicacionId) => {
    axios.post("rack/asignarUbicacionARackSlot", { ubicacionId, rackId: rack._id, columna: slot.columna, nivelRack: slot.nivelRack, profundidad: slot.profundidad }, { headers: headersToken })
      .then((res) => { if (res.data.success === 2) { message.success("Ubicación asignada al espacio del rack"); cargarDetalle(); } else message.error(res.data.error || "No se pudo asignar la ubicación"); })
      .catch(() => message.error("No se pudo asignar la ubicación"));
  };

  const quitarSlot = (ubicacionId) => {
    axios.post("rack/quitarUbicacionDelAcomodo", { ubicacionId }, { headers: headersToken })
      .then((res) => { if (res.data.success === 2) { message.success("Ubicación quitada del acomodo"); cargarDetalle(); } else message.error(res.data.error || "No se pudo quitar la ubicación"); })
      .catch(() => message.error("No se pudo quitar la ubicación"));
  };

  const desligar = (ubicacionId) => {
    axios.post("rack/desasignarUbicacionDeRack", { ubicacionId }, { headers: headersToken })
      .then((res) => { if (res.data.success === 2) { message.success("Ubicación desligada del rack"); cargarDetalle(); } else message.error(res.data.error || "No se pudo desligar"); })
      .catch(() => message.error("No se pudo desligar la ubicación"));
  };

  const crearEnSlot = (slot) => { setSlotPrecarga(slot); setModalAgregarVisible(true); };

  const vaciarAcomodo = () => {
    axios.post("rack/vaciarAcomodoRack", { rackId: rack._id }, { headers: headersToken })
      .then((res) => { if (res.data.success === 2) { message.success("Acomodo vaciado — coloca las ubicaciones a mano"); cargarDetalle(); } else message.error(res.data.error || "No se pudo vaciar el acomodo"); })
      .catch(() => message.error("No se pudo vaciar el acomodo"));
  };

  const ligarUbicacion = () => {
    if (!ubicacionALigarId || !rack) return;
    setLigando(true);
    const payload = { _id: ubicacionALigarId, rackId: rack._id };
    if (rack.nodosAccesoIds && rack.nodosAccesoIds.length > 0) payload.nodoAccesoId = rack.nodosAccesoIds[0];
    axios.post("ubicacionProyecto/editarUbicacionProyecto", payload, { headers: headersToken })
      .then((res) => { if (res.data.success === 2) { message.success("Ubicación ligada al rack"); setUbicacionALigarId(undefined); cargarDetalle(); } else message.error(res.data.error || "No se pudo ligar"); })
      .catch(() => message.error("No se pudo ligar la ubicación"))
      .finally(() => setLigando(false));
  };

  const esGrande = rack && (rack.numeroColumnas > LIMITE_GRID_DRAWER || rack.numeroNiveles > LIMITE_GRID_DRAWER);
  const mostrarMapa = enPagina || verMapa;

  const formConfig = rack ? (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: "#8a93a6", marginBottom: 4 }}>Código</div>
          <InputItem value={config.codigo} onChange={(e) => cambiarConfig("codigo", e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: "#8a93a6", marginBottom: 4 }}>Nombre</div>
          <InputItem value={config.nombre} onChange={(e) => cambiarConfig("nombre", e.target.value)} />
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: "#8a93a6", marginBottom: 4 }}>Columnas</div>
          <InputNumberItem style={{ width: "100%" }} min={1} value={config.numeroColumnas} onChange={(v) => cambiarConfig("numeroColumnas", v)} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: "#8a93a6", marginBottom: 4 }}>Niveles</div>
          <InputNumberItem style={{ width: "100%" }} min={1} value={config.numeroNiveles} onChange={(v) => cambiarConfig("numeroNiveles", v)} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: "#8a93a6", marginBottom: 4 }}>Profundidades</div>
          <InputNumberItem style={{ width: "100%" }} min={1} value={config.numeroProfundidades} onChange={(v) => cambiarConfig("numeroProfundidades", v)} />
        </div>
      </div>
      <div style={{ fontSize: 11, color: "#8a93a6", marginBottom: 4 }}>Tamaño físico del rack (m) — para el dibujo y validar que quepan</div>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: "#8a93a6", marginBottom: 4 }}>Ancho</div>
          <InputNumberItem style={{ width: "100%" }} min={0} step={0.1} value={config.anchoMetros} onChange={(v) => cambiarConfig("anchoMetros", v)} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: "#8a93a6", marginBottom: 4 }}>Alto</div>
          <InputNumberItem style={{ width: "100%" }} min={0} step={0.1} value={config.altoMetros} onChange={(v) => cambiarConfig("altoMetros", v)} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: "#8a93a6", marginBottom: 4 }}>Prof.</div>
          <InputNumberItem style={{ width: "100%" }} min={0} step={0.1} value={config.profundidadMetros} onChange={(v) => cambiarConfig("profundidadMetros", v)} />
        </div>
        <ButtonItem type="primary" loading={guardandoConfig} onClick={guardarConfig}>Guardar</ButtonItem>
      </div>
    </div>
  ) : null;

  const editarColapsable = (
    <Collapse bordered={false} style={{ background: "transparent", marginBottom: 8 }}>
      <Collapse.Panel key="editar" header="Editar rack (código, columnas, niveles, medidas…)">
        {formConfig}
      </Collapse.Panel>
    </Collapse>
  );

  const columnaMapa = mostrarMapa && rack && (
    <div style={{ flex: 1, minWidth: 0 }}>
      {!rackTieneMedidas(rack) ? (
        <>
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message="Configura el rack para acomodar"
            description="Captura el tamaño físico del rack (m) — y ajusta columnas/niveles si hace falta — para poder acomodar las ubicaciones y validar que quepan." />
          {formConfig}
        </>
      ) : !enPagina && esGrande ? (
        <>
          {editarColapsable}
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Rack grande"
            description={`Este rack es de ${rack.numeroColumnas}×${rack.numeroNiveles}. Para acomodarlo cómodamente ábrelo en pantalla completa.`}
          />
          {onConfigurarEnPagina && (
            <ButtonItem type="primary" icon={<AppstoreOutlined />} onClick={() => onConfigurarEnPagina(rack._id)}>
              Configurar en pantalla completa
            </ButtonItem>
          )}
        </>
      ) : (
        <>
          {editarColapsable}
          <div style={{ overflowX: "auto" }}>
            <RackMapaGrid
              rack={rack}
              ubicaciones={ubicaciones}
              ubicacionesSinRack={ubicacionesSinRack}
              ubicacionesPorColocar={ubicaciones.filter((u) => !(u.columna && u.nivelRack))}
              onAsignar={asignarSlot}
              onQuitar={quitarSlot}
              onDesligar={desligar}
              onCrearEnSlot={permisoAgregarUbicacion ? crearEnSlot : undefined}
              onDeshabilitar={permisoDeshabilitarUbicacion ? deshabilitar : undefined}
              onVaciar={vaciarAcomodo}
              puedeModificar={!!permisoAgregarUbicacion}
            />
          </div>
        </>
      )}
    </div>
  );

  const columnaLista = (
    <div
      style={{
        flex: mostrarMapa ? "0 0 380px" : "1 1 auto",
        width: mostrarMapa ? 380 : "100%",
        minWidth: 0,
        ...(mostrarMapa && rack ? { borderLeft: "1px solid #eef0f6", paddingLeft: 16 } : {}),
      }}
    >
      <Spin spinning={cargando}>
        {permisoAgregarUbicacion && rack && (
          <>
            <ButtonItem type="primary" icon={<PlusOutlined />} block style={{ marginBottom: 8 }} onClick={() => { setSlotPrecarga(null); setModalAgregarVisible(true); }}>
              Agregar nueva ubicación a este rack
            </ButtonItem>
            {ubicacionesSinRack.length > 0 ? (
              <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <SelectItem placeholder="Ligar ubicación existente..." width="100%" value={ubicacionALigarId} onChange={setUbicacionALigarId}>
                    {ubicacionesSinRack.map((u) => (
                      <Option key={u._id} value={u._id}>
                        {u.clave + " - " + u.nombre}{medidasTexto(u) ? `  (${medidasTexto(u)})` : ""}
                      </Option>
                    ))}
                  </SelectItem>
                </div>
                <div style={{ flexShrink: 0 }}>
                  <ButtonItem icon={<LinkOutlined />} lima loading={ligando} disabled={!ubicacionALigarId} onClick={ligarUbicacion}>Ligar</ButtonItem>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: "#8a93a6", marginBottom: 16 }}>No hay ubicaciones sin ligar en este almacén.</div>
            )}
          </>
        )}
        {ubicaciones.length === 0 ? (
          <Empty description="Este rack todavía no tiene ubicaciones" />
        ) : (
          <Collapse bordered={false} style={{ background: "transparent" }}>
            {ubicaciones.map((u) => (
              <Collapse.Panel
                key={String(u._id)}
                header={
                  <div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <strong>{u.clave}</strong>
                      <span onClick={(e) => e.stopPropagation()} style={{ display: "flex", gap: 6 }}>
                        {permisoAgregarUbicacion && u.columna && u.nivelRack && (
                          <Tooltip title="Quitar del acomodo (queda como 'por colocar')">
                            <ButtonItem lila icon={<DisconnectOutlined />} style={{ height: 20, width: 20, padding: 0 }} onClick={() => quitarSlot(u._id)} />
                          </Tooltip>
                        )}
                        {permisoAgregarUbicacion && (
                          <Popconfirm
                            title="¿Desligar del rack? Dejará de pertenecer a este rack (no se borra; queda disponible para otro)."
                            okText="Desligar"
                            cancelText="Cancelar"
                            onConfirm={() => desligar(u._id)}
                          >
                            <Tooltip title="Desligar del rack (más suave que deshabilitar)">
                              <ButtonItem naranja icon={<ExportOutlined />} style={{ height: 20, width: 20, padding: 0 }} />
                            </Tooltip>
                          </Popconfirm>
                        )}
                        {permisoDeshabilitarUbicacion && (
                          <Popconfirm title="¿Deshabilitar esta ubicación?" okText="Sí" cancelText="No" onConfirm={() => deshabilitar(u._id)}>
                            <ButtonItem danger icon={<DeleteOutlined />} style={{ height: 20, width: 20, padding: 0 }} />
                          </Popconfirm>
                        )}
                      </span>
                    </div>
                    <div style={{ marginTop: 6 }}>
                      <Tag>Columna {u.columna}</Tag>
                      <Tag>Nivel {u.nivelRack}</Tag>
                      <Tag>Prof. {u.profundidad}</Tag>
                      <Tag color={u.insumos?.length ? "blue" : "default"}>{u.insumos?.length || 0} insumo(s)</Tag>
                      {medidasTexto(u) && <Tag color="geekblue">{medidasTexto(u)}</Tag>}
                      {rackTieneMedidas(rack) && !cabeEnRack(u, dimensionesRack(rack)) && <Tag color="red">No cabe en el rack</Tag>}
                    </div>
                  </div>
                }
              >
                {!u.insumos || u.insumos.length === 0 ? (
                  <div style={{ fontSize: 12, color: "#999" }}>Sin existencia registrada en esta ubicación</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {u.insumos.map((i) => (
                      <div key={i.insumoId} style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                        <span>{i.clave} - {i.descripcion}</span>
                        <strong>{i.cantidad}</strong>
                      </div>
                    ))}
                  </div>
                )}
              </Collapse.Panel>
            ))}
          </Collapse>
        )}
      </Spin>
    </div>
  );

  return (
    <>
      {!enPagina && rack && (
        <div style={{ marginBottom: 12 }}>
          <ButtonItem azulMarino icon={<AppstoreOutlined />} onClick={() => setVerMapa((v) => !v)}>
            {verMapa ? "Ocultar mapa del rack" : "Ver mapa del rack"}
          </ButtonItem>
        </div>
      )}

      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        {columnaMapa}
        {columnaLista}
      </div>

      <AgregarUbicacion
        visible={modalAgregarVisible}
        onClose={cerrarModalAgregar}
        proyectoId={proyectoId}
        rackMode
        rackId={rack?._id}
        nodoAccesoId={rack?.nodosAccesoIds?.[0]}
        columna={slotPrecarga?.columna}
        nivelRack={slotPrecarga?.nivelRack}
        profundidad={slotPrecarga?.profundidad}
      />
    </>
  );
};

export default RackAcomodo;
